import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useHealthAndSafetyService } from '../../../../apis/healthAndSafety/useHealthAndSafetyService';
import {
  THealthCheckup,
  THealthCheckupListApiResponse,
  THealthCheckupParams,
} from '../../../../apis/healthAndSafety/healthAndSafety.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getHealthCheckupListKey = 'get-health-checkup-list';
export const getHealthCheckupByIdKey = 'get-health-checkup-by-id';

// ===================== LIST HOOK =====================
export const useHealthCheckupListHook = (
  params: THealthCheckupParams,
  options?: UseQueryOptions<THealthCheckupListApiResponse, TQueryError>
): UseQueryResult<THealthCheckupListApiResponse, TQueryError> => {
  const { getHealthCheckupList } = useHealthAndSafetyService();

  return useQuery<THealthCheckupListApiResponse, TQueryError>({
    queryKey: [getHealthCheckupListKey, params],
    queryFn: () => getHealthCheckupList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useHealthCheckupByIdHook = (
  id: string,
  options?: UseQueryOptions<THealthCheckup, TQueryError>
): UseQueryResult<THealthCheckup, TQueryError> => {
  const { getHealthCheckupById } = useHealthAndSafetyService();

  return useQuery<THealthCheckup, TQueryError>({
    queryKey: [getHealthCheckupByIdKey, id],
    queryFn: () => getHealthCheckupById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateHealthCheckupHook = (
  options?: UseMutationOptions<
    THealthCheckup,
    TMutationError,
    Omit<THealthCheckup, 'id' | 'createdAt' | 'updatedAt'>
  >
): UseMutationResult<
  THealthCheckup,
  TMutationError,
  Omit<THealthCheckup, 'id' | 'createdAt' | 'updatedAt'>
> => {
  const { createHealthCheckup } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    THealthCheckup,
    TMutationError,
    Omit<THealthCheckup, 'id' | 'createdAt' | 'updatedAt'>
  >({
    mutationFn: (checkup) => createHealthCheckup(checkup),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getHealthCheckupListKey] });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateHealthCheckupHook = (
  options?: UseMutationOptions<
    THealthCheckup,
    TMutationError,
    { id: string; data: Partial<THealthCheckup> }
  >
): UseMutationResult<
  THealthCheckup,
  TMutationError,
  { id: string; data: Partial<THealthCheckup> }
> => {
  const { updateHealthCheckup } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    THealthCheckup,
    TMutationError,
    { id: string; data: Partial<THealthCheckup> }
  >({
    mutationFn: ({ id, data }) => updateHealthCheckup(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [getHealthCheckupListKey] });
      queryClient.invalidateQueries({
        queryKey: [getHealthCheckupByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteHealthCheckupHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteHealthCheckup } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteHealthCheckup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getHealthCheckupListKey] });
    },
    ...options,
  });
};
